import { Box, Button, IconButton, List, ListItem, ListItemText, TextField, Typography } from "@mui/material"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import { FieldPath, arrayRemove, deleteField, doc, getDoc, setDoc, updateDoc } from "firebase/firestore"
import { useEffect, useState } from "react"

import { db } from "../../firebase"
import { PopupService } from "react-ui-components/components/PopupService"

const LOCATIONS_PATH = "app-configurations/availableLocations"

type LocationData = {
  [country: string]: {
    [state: string]: string[]
  }
}

const normalizeLocationName = (value: string) => value.trim()

export const renameCountryInLocations = (
  locations: LocationData,
  country: string,
  nextCountryValue: string,
) => {
  const nextCountry = normalizeLocationName(nextCountryValue)
  if (!nextCountry || nextCountry === country || !locations[country]) {
    return locations
  }

  const { [country]: countryLocations, ...rest } = locations
  return {
    ...rest,
    [nextCountry]: {
      ...(rest[nextCountry] || {}),
      ...(countryLocations || {}),
    },
  }
}

export const renameStateInLocations = (
  locations: LocationData,
  country: string,
  state: string,
  nextStateValue: string,
) => {
  const nextState = normalizeLocationName(nextStateValue)
  const countryLocations = locations[country] || {}
  if (!nextState || nextState === state || !countryLocations[state]) {
    return locations
  }

  const { [state]: cities, ...restStates } = countryLocations
  return {
    ...locations,
    [country]: {
      ...restStates,
      [nextState]: Array.from(new Set([...(restStates[nextState] || []), ...(cities || [])])),
    },
  }
}

export const renameCityInLocations = (
  locations: LocationData,
  country: string,
  state: string,
  city: string,
  nextCityValue: string,
) => {
  const nextCity = normalizeLocationName(nextCityValue)
  const cities = locations[country]?.[state] || []
  if (!nextCity || nextCity === city || !cities.includes(city)) {
    return locations
  }

  return {
    ...locations,
    [country]: {
      ...(locations[country] || {}),
      [state]: Array.from(new Set(cities.map((currentCity) => currentCity === city ? nextCity : currentCity))),
    },
  }
}

/**
 * Bespoke editor for app-configurations/availableLocations. This is a
 * single nested Firestore document, not a flat collection, so it cannot go
 * through DataProvider/ListComponent like businesses-config.ts does -
 * see BusinessAdsConfig.tsx for the precedent of a bespoke single-doc editor.
 */
export const LocationsComponent = () => {
  const [locations, setLocations] = useState<LocationData>({})
  const [loading, setLoading] = useState(false)
  const [newCountry, setNewCountry] = useState("")
  const [newState, setNewState] = useState("")
  const [newCity, setNewCity] = useState("")

  const reload = async () => {
    const snap = await getDoc(doc(db, LOCATIONS_PATH))
    setLocations((snap.data() as LocationData) || {})
  }

  useEffect(() => {
    reload()
  }, [])

  const handleAdd = async () => {
    if (!newCountry.trim()) {
      return
    }
    setLoading(true)
    try {
      const ref = doc(db, LOCATIONS_PATH)
      const country = newCountry.trim()
      const state = newState.trim()
      const city = newCity.trim()

      if (state && city) {
        await setDoc(ref, { [country]: { [state]: [city] } }, { merge: true })
      } else if (state) {
        await setDoc(ref, { [country]: { [state]: [] } }, { merge: true })
      } else {
        await setDoc(ref, { [country]: {} }, { merge: true })
      }

      setNewCountry("")
      setNewState("")
      setNewCity("")
      await reload()
      PopupService.pushPopup("Location saved.")
    } catch (error: any) {
      PopupService.pushPopup(`Failed to save location: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteCountry = async (country: string) => {
    setLoading(true)
    try {
      await updateDoc(doc(db, LOCATIONS_PATH), new FieldPath(country), deleteField())
      await reload()
      PopupService.pushPopup("Country deleted.")
    } catch (error: any) {
      PopupService.pushPopup(`Failed to delete country: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteState = async (country: string, state: string) => {
    setLoading(true)
    try {
      await updateDoc(doc(db, LOCATIONS_PATH), new FieldPath(country, state), deleteField())
      await reload()
      PopupService.pushPopup("State/province deleted.")
    } catch (error: any) {
      PopupService.pushPopup(`Failed to delete state/province: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteCity = async (country: string, state: string, city: string) => {
    setLoading(true)
    try {
      await updateDoc(doc(db, LOCATIONS_PATH), new FieldPath(country, state), arrayRemove(city))
      await reload()
      PopupService.pushPopup("City deleted.")
    } catch (error: any) {
      PopupService.pushPopup(`Failed to delete city: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleRenameCountry = async (country: string) => {
    const nextCountry = window.prompt("Edit country", country)
    if (nextCountry === null) {
      return
    }

    const nextLocations = renameCountryInLocations(locations, country, nextCountry)
    if (nextLocations === locations) {
      return
    }

    setLoading(true)
    try {
      await setDoc(doc(db, LOCATIONS_PATH), nextLocations)
      await reload()
      PopupService.pushPopup("Country updated.")
    } catch (error: any) {
      PopupService.pushPopup(`Failed to update country: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleRenameState = async (country: string, state: string) => {
    const nextState = window.prompt("Edit state/province", state)
    if (nextState === null) {
      return
    }

    const nextLocations = renameStateInLocations(locations, country, state, nextState)
    if (nextLocations === locations) {
      return
    }

    setLoading(true)
    try {
      await setDoc(doc(db, LOCATIONS_PATH), nextLocations)
      await reload()
      PopupService.pushPopup("State/province updated.")
    } catch (error: any) {
      PopupService.pushPopup(`Failed to update state/province: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleRenameCity = async (country: string, state: string, city: string) => {
    const nextCity = window.prompt("Edit city", city)
    if (nextCity === null) {
      return
    }

    const nextLocations = renameCityInLocations(locations, country, state, city, nextCity)
    if (nextLocations === locations) {
      return
    }

    setLoading(true)
    try {
      await setDoc(doc(db, LOCATIONS_PATH), nextLocations)
      await reload()
      PopupService.pushPopup("City updated.")
    } catch (error: any) {
      PopupService.pushPopup(`Failed to update city: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Locations
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
        <TextField label="Country" value={newCountry} onChange={(e) => setNewCountry(e.target.value)} size="small" />
        <TextField label="State/Province" value={newState} onChange={(e) => setNewState(e.target.value)} size="small" />
        <TextField label="City" value={newCity} onChange={(e) => setNewCity(e.target.value)} size="small" disabled={!newState} />
        <Button variant="contained" onClick={handleAdd} disabled={loading || !newCountry.trim()}>
          Add
        </Button>
      </Box>

      {Object.keys(locations).length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No locations configured yet.
        </Typography>
      )}

      {Object.entries(locations).map(([country, states]) => (
        <Box key={country} sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {country}
            </Typography>
            <Box>
              <IconButton size="small" onClick={() => handleRenameCountry(country)} disabled={loading} aria-label={`Edit ${country}`}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => handleDeleteCountry(country)} disabled={loading} aria-label={`Delete ${country}`}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <List dense>
            {Object.entries(states || {}).map(([state, cities]) => (
              <Box key={state} sx={{ pl: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <ListItemText primary={state} />
                  <Box>
                    <IconButton size="small" onClick={() => handleRenameState(country, state)} disabled={loading} aria-label={`Edit ${state}`}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteState(country, state)} disabled={loading} aria-label={`Delete ${state}`}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                {(cities || []).map((city) => (
                  <ListItem
                    key={city}
                    sx={{ pl: 4 }}
                    secondaryAction={
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleRenameCity(country, state, city)}
                          disabled={loading}
                          aria-label={`Edit ${city}`}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteCity(country, state, city)}
                          disabled={loading}
                          aria-label={`Delete ${city}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText primary={city} />
                  </ListItem>
                ))}
              </Box>
            ))}
          </List>
        </Box>
      ))}
    </Box>
  )
}

export default LocationsComponent
